import { ReactNode } from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  tooltip?: string;
}

export function StatsCard({ title, value, icon, tooltip }: StatsCardProps) {
  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 2.5 },
        minHeight: '80px',
        height: '100%',
        background: '#ffffff',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        boxShadow: '0 3px 12px rgba(0, 0, 0, 0.12)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        '@media (hover: hover)': {
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
          },
        },
      }}
      elevation={0}
    >
      <Box display="flex" alignItems="center" height="100%">
        {/* Icon Column */}
        <Box
          sx={{
            p: 1,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            height: '40px',
            mr: { xs: 1.5, sm: 2 },
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        {/* Content Column */}
        <Box flex={1} minWidth={0}>
          <Typography
            variant="h5"
            component="div"
            fontWeight="bold"
            sx={{
              mb: 0.5,
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              lineHeight: 1,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              wordBreak: 'break-word',
            }}
          >
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              sx={{ lineHeight: 1.2 }}
            >
              {title}
            </Typography>
            {tooltip && (
              <Tooltip title={tooltip} arrow placement="top">
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                    cursor: 'help',
                    opacity: 0.7,
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      opacity: 1,
                      color: 'primary.main',
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
